import {Injectable} from '@angular/core';

import {Observable} from 'rxjs';

import {
  ChartRefineResponse,
  ReportAnalysis,
  ReportAnalyticsRequest
} from './report-analytics.interfaces';

import {ChartSpec} from '../../shared/charts/chart-spec/chart-spec.interfaces';
import {ApiService} from '../../shared/api';

const JOBS_PATH = '/report-analytics/jobs';

/**
 * Starts and reads AI analyses of dynamic reports; keeps finished analyses, including refined charts and the selected
 * chart, for the page session, keyed by report definition.
 */
@Injectable()
export class ReportAnalyticsService {

  private readonly analysesByDefinition = new Map<string, ReportAnalysis>();
  private readonly selectionsByDefinition = new Map<string, number>();

  constructor(private api: ApiService) {
  }

  public static definitionKey(request: ReportAnalyticsRequest): string {
    return JSON.stringify([request.document, request.query]);
  }

  public start(request: ReportAnalyticsRequest): Observable<string> {
    return this.api.post(`/elasticquery/api/v1/search/${request.document}/ai-analysis`,
      {...request.query, reportName: request.reportName});
  }

  public get(jobId: string): Observable<ReportAnalysis> {
    return this.api.ai.silent.get(`${JOBS_PATH}/${jobId}`);
  }

  public refine(jobId: string, currentSpec: ChartSpec, instruction: string): Observable<ChartRefineResponse> {
    return this.api.ai.post(`${JOBS_PATH}/${jobId}/refine`, {currentSpec, instruction});
  }

  public cached(request: ReportAnalyticsRequest): ReportAnalysis | undefined {
    return this.analysesByDefinition.get(ReportAnalyticsService.definitionKey(request));
  }

  public remember(request: ReportAnalyticsRequest, analysis: ReportAnalysis): void {
    this.analysesByDefinition.set(ReportAnalyticsService.definitionKey(request), analysis);
  }

  public cachedSelection(request: ReportAnalyticsRequest): number {
    return this.selectionsByDefinition.get(ReportAnalyticsService.definitionKey(request)) ?? 0;
  }

  public rememberSelection(request: ReportAnalyticsRequest, selectedIndex: number): void {
    this.selectionsByDefinition.set(ReportAnalyticsService.definitionKey(request), selectedIndex);
  }

  public forget(request: ReportAnalyticsRequest): void {
    const key = ReportAnalyticsService.definitionKey(request);
    this.analysesByDefinition.delete(key);
    this.selectionsByDefinition.delete(key);
  }
}
